import { useEffect, useState, FormEvent } from 'react';
import { api, type Me, type SpokeRow, type InstallLink, type SpokeCommandRow, type SpokeCommandType } from '../lib/api';
import CiscoStatusCallout from '../components/CiscoStatusCallout';

function timeAgo(ms?: number | null) {
  if (!ms) return 'never';
  const secs = Math.max(1, Math.floor((Date.now() - ms) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// M9: prefer the server-derived status_pill (green/yellow/red/gray). Fall back
// to local derivation if the server hasn't been redeployed with migration 010 yet.
function statusPill(s: SpokeRow) {
  const pill = s.status_pill ?? localPill(s.last_known_status, s.last_heartbeat_at, s.errors_count ?? 0);
  const ageSec = s.seconds_since_heartbeat ?? (s.last_heartbeat_at ? Math.floor((Date.now() - s.last_heartbeat_at) / 1000) : null);
  const ageLabel = ageSec === null ? 'never' : ageSec < 60 ? `${ageSec}s` : ageSec < 3600 ? `${Math.floor(ageSec/60)}m` : `${Math.floor(ageSec/3600)}h`;
  const dot = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-rose-500', gray: 'bg-gray-400' }[pill];
  const text = { green: 'connected', yellow: 'degraded', red: 'offline', gray: 'never seen' }[pill];
  const cls  = { green: 'pill-ok', yellow: 'pill-warn', red: 'pill-err', gray: 'pill-warn' }[pill];
  return (
    <span className={`${cls} inline-flex items-center gap-1.5`}>
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />{text} · {ageLabel}
    </span>
  );
}

function localPill(status: string, last_heartbeat_at: number | null, errors_count: number): 'green' | 'yellow' | 'red' | 'gray' {
  if (!last_heartbeat_at) return 'gray';
  const ageMs = Date.now() - last_heartbeat_at;
  if (ageMs > 5 * 60 * 1000) return 'red';
  if (ageMs > 2 * 60 * 1000) return 'yellow';
  if (errors_count > 0) return 'yellow';
  if (status === 'operational') return 'green';
  if (status === 'degraded') return 'yellow';
  if (status === 'major_outage' || status === 'partial_outage') return 'red';
  return 'yellow';
}

// HA role + state badge — shows "Spoke 1 (HA master)" etc. and the live
// active/standby state the Hub arbitrator computed.
function haBadge(s: SpokeRow) {
  const role = s.ha_role ?? 'unassigned';
  const state = s.ha_state ?? 'none';
  const preempt = s.preempt === 1;
  const label =
    role === 'primary'   ? 'Spoke 1 · HA Primary' :
    role === 'secondary' ? 'Spoke 2 · HA Secondary' :
    role === 'single'    ? 'Single node' :
                            'Unassigned';
  const roleCls =
    role === 'primary'   ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' :
    role === 'secondary' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' :
    role === 'single'    ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' :
                            'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  const stateBadge = state === 'active'
    ? <span className="pill-ok text-[10px]">ACTIVE</span>
    : state === 'standby'
    ? <span className="pill-warn text-[10px]">STANDBY</span>
    : null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`pill ${roleCls}`}>
        {label}{role === 'primary' && preempt ? ' · preempt=1' : role === 'secondary' && !preempt ? ' · preempt=0' : ''}
      </span>
      {stateBadge}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tunnel Connectors — the spoke-less path. Shows the CUCM/UCCX tunnel services
// + live status (from connector_status), and generates a downloadable
// cloudflared installer the operator runs on any customer desktop. Reusable
// per tenant: set the prefix + CUCM/UCCX IPs and download the script.
// ---------------------------------------------------------------------------
const TUNNEL_API = (import.meta as any).env?.VITE_API_BASE ?? 'https://api.trinethub.com';

function ps1Installer(prefix: string, cucmIp: string, uccxIp: string): string {
  const T = prefix + '-connector';
  return [
    '# TriNetHub Tunnel Connector installer (Windows). In PowerShell run:',
    '#   Set-ExecutionPolicy -Scope Process Bypass -Force ; .\\install-tunnel-connector.ps1',
    '$ErrorActionPreference = "Stop"',
    '$Dir = "C:\\cloudflared"; New-Item -ItemType Directory -Force -Path $Dir | Out-Null',
    '$Exe = "$Dir\\cloudflared.exe"',
    'if (-not (Test-Path $Exe)) { Invoke-WebRequest "https://github.com/cloudflare/cloudflared/releases/latest/download/cloudflared-windows-amd64.exe" -OutFile $Exe }',
    '& $Exe tunnel login',
    '& $Exe tunnel create ' + T + ' 2>$null',
    '$cfg = "tunnel: ' + T + '`n" +',
    '  "protocol: http2`nno-autoupdate: true`ningress:`n" +',
    '  "  - hostname: cucm-' + prefix + '.trinethub.com`n    service: https://' + cucmIp + ':8443`n    originRequest:`n      noTLSVerify: true`n      httpHostHeader: ' + cucmIp + '`n" +',
    '  "  - hostname: uccx-' + prefix + '.trinethub.com`n    service: https://' + uccxIp + ':8443`n    originRequest:`n      noTLSVerify: true`n      httpHostHeader: ' + uccxIp + '`n" +',
    '  "  - service: http_status:404`n"',
    'Set-Content -Encoding ASCII "$Dir\\config.yml" $cfg',
    '& $Exe tunnel route dns ' + T + ' cucm-' + prefix + '.trinethub.com',
    '& $Exe tunnel route dns ' + T + ' uccx-' + prefix + '.trinethub.com',
    'Write-Host "Connector ready. Starting tunnel (leave this window open):"',
    '& $Exe tunnel --config "$Dir\\config.yml" run ' + T,
  ].join('\r\n');
}

function shInstaller(prefix: string, cucmIp: string, uccxIp: string): string {
  const T = prefix + '-connector';
  return [
    '#!/usr/bin/env bash',
    '# TriNetHub Tunnel Connector installer (Linux). Run:  bash install-tunnel-connector.sh',
    'set -euo pipefail',
    'ARCH=$(dpkg --print-architecture 2>/dev/null || echo amd64)',
    'curl -fsSL "https://github.com/cloudflare/cloudflared/releases/latest/download/cloudflared-linux-${ARCH}" -o ~/cloudflared && chmod +x ~/cloudflared',
    '~/cloudflared tunnel login',
    '~/cloudflared tunnel create ' + T + ' || true',
    'mkdir -p ~/.cloudflared',
    'cat > ~/.cloudflared/config.yml <<EOF',
    'tunnel: ' + T,
    'protocol: http2',
    'ingress:',
    '  - hostname: cucm-' + prefix + '.trinethub.com',
    '    service: https://' + cucmIp + ':8443',
    '    originRequest: { noTLSVerify: true, httpHostHeader: "' + cucmIp + '" }',
    '  - hostname: uccx-' + prefix + '.trinethub.com',
    '    service: https://' + uccxIp + ':8443',
    '    originRequest: { noTLSVerify: true, httpHostHeader: "' + uccxIp + '" }',
    '  - service: http_status:404',
    'EOF',
    '~/cloudflared tunnel route dns ' + T + ' cucm-' + prefix + '.trinethub.com',
    '~/cloudflared tunnel route dns ' + T + ' uccx-' + prefix + '.trinethub.com',
    '~/cloudflared tunnel --config ~/.cloudflared/config.yml run ' + T,
  ].join('\n');
}

function download(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

interface ConnRow { status: string; cucm_reachable: number; cucm_version: string; uccx_reachable: number; phones: number; last_checked_at: number; detail: string }

function TunnelConnectors({ me }: { me: Me }) {
  const [conn, setConn] = useState<ConnRow | null>(null);
  const [prefix, setPrefix] = useState('lb');
  const [cucmIp, setCucmIp] = useState('10.18.160.26');
  const [uccxIp, setUccxIp] = useState('10.18.160.48');
  const canManage = me.role === 'super_admin' || me.role === 'tenant_admin';

  useEffect(() => {
    let cancel = false;
    const load = () => fetch(`${TUNNEL_API}/api/migration/connector-status`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null).then((j) => { if (!cancel && j) setConn(j.connector); }).catch(() => {});
    load(); const t = setInterval(load, 60_000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const online = conn?.status === 'online';
  const row = (label: string, svc: string, up: boolean, extra?: string) => (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${up ? 'bg-emerald-500' : 'bg-rose-500'}`} />
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-gray-500 font-mono">{svc}{extra ? ` · ${extra}` : ''}</div>
      </div>
      <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full font-semibold ${up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{up ? 'reachable' : 'unreachable'}</span>
    </div>
  );

  return (
    <div className="card p-4 border-tnh-accent/30 bg-tnh-accent/[0.03]">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-tnh-accent">Tunnel Connectors</h2>
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${online ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>{conn ? (online ? 'online' : 'offline') : '…'}</span>
        <span className="ml-auto text-[11px] text-gray-500">{conn ? `checked ${timeAgo(conn.last_checked_at)}` : ''}</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">Spoke-less path — the customer runs only <span className="font-mono">cloudflared</span>; the Hub reaches CUCM/UCCX over the tunnel. No Docker, no inbound ports.</p>

      <div className="grid sm:grid-cols-2 gap-2 mb-4">
        {row('CUCM tunnel', 'cucm-tunnel-svc', !!conn?.cucm_reachable, conn ? `CUCM ${conn.cucm_version || '?'} · ${(conn.phones || 0).toLocaleString()} phones` : undefined)}
        {row('UCCX tunnel', 'uccx-tunnel-svc', !!conn?.uccx_reachable, conn?.uccx_reachable ? 'reachable via tunnel' : undefined)}
      </div>

      {canManage && (
        <div className="rounded-lg border border-tnh-border dark:border-tnh-border-dark p-3">
          <div className="text-xs font-semibold mb-2">Connector installer — run on any customer desktop on the Cisco LAN</div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <label className="text-xs text-gray-500">Prefix
              <input value={prefix} onChange={(e) => setPrefix(e.target.value.replace(/[^a-z0-9-]/gi, ''))} className="mt-1 block w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" /></label>
            <label className="text-xs text-gray-500">CUCM IP
              <input value={cucmIp} onChange={(e) => setCucmIp(e.target.value)} className="mt-1 block w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" /></label>
            <label className="text-xs text-gray-500">UCCX IP
              <input value={uccxIp} onChange={(e) => setUccxIp(e.target.value)} className="mt-1 block w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" /></label>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => download(`install-tunnel-connector-${prefix}.ps1`, ps1Installer(prefix, cucmIp, uccxIp))} className="btn-primary text-sm">Download Windows installer (.ps1)</button>
            <button onClick={() => download(`install-tunnel-connector-${prefix}.sh`, shInstaller(prefix, cucmIp, uccxIp))} className="btn-ghost text-sm">Download Linux installer (.sh)</button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">Publishes <span className="font-mono">cucm-{prefix}.trinethub.com</span> + <span className="font-mono">uccx-{prefix}.trinethub.com</span>. Reusable for any tenant — change the prefix + IPs.</p>
        </div>
      )}
    </div>
  );
}

export default function Spokes({ me }: { me: Me }) {
  const [list, setList] = useState<SpokeRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [token, setToken] = useState<{ token: string; tenant_id: string; install_hint: string; expires_at: number } | null>(null);
  const [selected, setSelected] = useState<SpokeRow | null>(null);

  async function load() {
    try { setErr(null); const r = await api.spokesList(); setList(r.spokes); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, []);

  async function revoke(s: SpokeRow) {
    if (!confirm(`Revoke spoke "${s.spoke_id}"? It will stop being able to send heartbeats and CDR.`)) return;
    try { await api.spokesRevoke(s.spoke_id); await load(); setSelected(null); }
    catch (e: any) { setErr(e.message); }
  }

  const canBootstrap = me.role === 'super_admin' || me.role === 'tenant_admin';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Spokes</h1>
          <p className="text-gray-500 text-sm mt-1">
            Customer-side agents that bridge the Hub to on-prem Cisco/Webex systems. Each spoke heartbeats every 60s and uploads CDR.
          </p>
        </div>
        {canBootstrap && (
          <button onClick={() => setShowBootstrap(true)} className="btn-ghost text-sm">Advanced: raw bootstrap token</button>
        )}
      </div>

      <TunnelConnectors me={me} />

      {canBootstrap && <SpokeInstallPanel me={me} />}

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      {showBootstrap && <BootstrapModal onClose={() => setShowBootstrap(false)} onCreated={(t) => { setToken(t); setShowBootstrap(false); load(); }} me={me} />}

      {token && <TokenReveal token={token} onDismiss={() => setToken(null)} />}

      {list === null && !err && <div className="card p-8 text-center text-gray-500">Loading…</div>}
      {list && list.length === 0 && (
        <div className="card p-12 text-center text-gray-500">
          <div className="font-medium">No spokes connected yet.</div>
          <div className="text-sm mt-1">
            {canBootstrap ? 'Generate a bootstrap token above, then run install-spoke.sh on the customer machine.' : 'A super-admin or tenant-admin needs to bootstrap one.'}
          </div>
        </div>
      )}

      <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark">
        {list?.map((s) => (
          <div key={s.spoke_id} onClick={() => setSelected(s)}
               className="px-4 py-3 cursor-pointer hover:bg-tnh-accent/[0.03] transition">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono font-medium text-sm">{s.spoke_id}</span>
              {statusPill(s)}
              {haBadge(s)}
              <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{s.profile}</span>
              <span className="text-xs text-gray-500">tenant: <span className="font-mono">{s.tenant_id}</span></span>
            </div>
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
              <span>bootstrapped {timeAgo(s.bootstrap_at)}</span>
              <span>·</span>
              <span>last beat {timeAgo(s.last_heartbeat_at)}</span>
              <span>·</span>
              <span>agent v{s.agent_version || '?'}</span>
              <span>·</span>
              <span>config v{s.config_version}</span>
              {s.errors_count && s.errors_count > 0 ? (
                <>
                  <span>·</span>
                  <span className="text-tnh-err">errors: {s.errors_count}</span>
                </>
              ) : null}
            </div>
            {s.error_summary && s.error_summary.length > 0 && (
              <div className="text-xs text-tnh-err mt-1 truncate">last error: {s.error_summary}</div>
            )}
          </div>
        ))}
      </div>

      {selected && <SpokeDetailModal spoke={selected} me={me} onClose={() => setSelected(null)} onRevoke={revoke} />}
    </div>
  );
}

function BootstrapModal({ onClose, onCreated, me }: { onClose: () => void; onCreated: (t: any) => void; me: Me }) {
  const [tenantId, setTenantId] = useState<string>(me.role === 'super_admin' ? 'longbeach' : me.tenant_id);
  const [profile, setProfile] = useState<'production_ha' | 'compact_ha' | 'single_node'>('single_node');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const t = await api.spokesBootstrap({ tenant_id: tenantId, profile, notes: notes || undefined });
      onCreated(t);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-semibold text-lg">Generate bootstrap token</h2>
        <p className="text-sm text-gray-500">A one-time token that the spoke uses to claim its identity. Expires in 24h. Show it to the operator once — they paste it into <code className="font-mono">install-spoke.sh</code>.</p>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tenant ID</label>
          <input value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={me.role !== 'super_admin'}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent font-mono"
                 required />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Profile</label>
          <select value={profile} onChange={(e) => setProfile(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
            <option value="single_node">Single node (99.0% SLA)</option>
            <option value="compact_ha">Compact HA (99.5% SLA)</option>
            <option value="production_ha">Production HA (99.9% SLA)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent"
                 placeholder="e.g. Long Beach pilot install — Tuesday onsite" />
        </div>
        {err && <div className="text-sm text-tnh-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Generating…' : 'Generate'}</button>
        </div>
      </form>
    </div>
  );
}

function TokenReveal({ token, onDismiss }: { token: { token: string; tenant_id: string; install_hint: string; expires_at: number }; onDismiss: () => void }) {
  const expires = new Date(token.expires_at).toLocaleString();
  const installerCmd =
    `bash /mnt/c/Users/ahhamad/Downloads/TriNetHub/infra/spoke/install-spoke.sh --token ${token.token}`;
  return (
    <div className="card p-5 border-tnh-accent bg-tnh-accent/[0.04] space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-tnh-accent font-semibold">Bootstrap token (one-time)</div>
          <div className="font-mono text-sm mt-1 break-all">{token.token}</div>
          <div className="text-xs text-gray-500 mt-1">tenant <span className="font-mono">{token.tenant_id}</span> · expires {expires}</div>
        </div>
        <button onClick={onDismiss} className="btn-ghost text-xs">Dismiss</button>
      </div>
      <div>
        <div className="text-xs font-semibold mb-1">Install on the SpokeLB machine (WSL):</div>
        <pre className="p-3 bg-gray-900 text-gray-100 text-xs rounded overflow-x-auto font-mono whitespace-pre-wrap break-all">{installerCmd}</pre>
        <div className="text-xs text-gray-500 mt-2">
          Running folder: <span className="font-mono">C:\Users\ahhamad\Downloads\SpokeLB</span> (this is the default — override with <span className="font-mono">--install-dir</span>).
        </div>
      </div>
    </div>
  );
}

/**
 * Top-level installer panel — two paths:
 *  1. Install link (RECOMMENDED, default): Tenant-admin creates a public link
 *     bound to a specific email. Operator runs one curl on the target host.
 *     Bootstrap prompts for email + 6-digit OTP. Only then does the API return
 *     the real installer + bootstrap token. Token never sits in a file.
 *  2. Direct download (advanced): existing flow — downloads a .sh with the
 *     token baked in. Kept for air-gapped scenarios where the host can't
 *     reach the Hub interactively.
 */
function SpokeInstallPanel({ me }: { me: Me }) {
  const [tab, setTab] = useState<'link' | 'download'>('link');
  return (
    <div className="card p-5 bg-gradient-to-br from-tnh-accent/5 to-transparent border-tnh-accent/30 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-tnh-accent font-semibold">Install a spoke</div>
        <div className="font-medium mt-1">Two paths. The link path is more secure — the token never leaves the Hub.</div>
      </div>
      <div className="flex items-center gap-1 border-b border-tnh-border dark:border-tnh-border-dark text-sm">
        <button onClick={() => setTab('link')} className={`px-4 py-2 -mb-px border-b-2 ${tab === 'link' ? 'border-tnh-accent text-tnh-accent font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
          🔒 Install via secure link <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">recommended</span>
        </button>
        <button onClick={() => setTab('download')} className={`px-4 py-2 -mb-px border-b-2 ${tab === 'download' ? 'border-tnh-accent text-tnh-accent font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
          📥 Download installer <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">advanced</span>
        </button>
      </div>
      {tab === 'link'     ? <InstallLinkPanel me={me} /> : <DownloadInstallerPanel me={me} />}
    </div>
  );
}

function InstallLinkPanel({ me }: { me: Me }) {
  const [slot, setSlot] = useState<'single' | 'master' | 'slave'>('single');
  const [tenantId, setTenantId] = useState<string>(me.role === 'super_admin' ? 'longbeach' : me.tenant_id);
  const [profile, setProfile] = useState<'single_node' | 'compact_ha' | 'production_ha'>('single_node');
  const [allowedEmail, setAllowedEmail] = useState<string>(me.email);
  const [label, setLabel] = useState<string>('');
  const [ttlHours, setTtlHours] = useState<number>(24);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<{ oneliner: string; public_url: string; allowed_email: string; expires_at: number; install_id: string } | null>(null);
  const [links, setLinks] = useState<InstallLink[]>([]);

  useEffect(() => { if (slot === 'single') setProfile('single_node'); else if (profile === 'single_node') setProfile('production_ha'); }, [slot]);
  async function reloadLinks() {
    try { const r = await api.spokeInstallLinkList(me.role === 'super_admin' ? undefined : me.tenant_id); setLinks(r.install_links); } catch {}
  }
  useEffect(() => { reloadLinks(); }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setCreated(null);
    try {
      const r = await api.spokeInstallLinkCreate({
        tenant_id: tenantId, spoke_slot: slot, profile, allowed_email: allowedEmail,
        label: label || undefined, ttl_hours: ttlHours, max_uses: 1,
      });
      setCreated(r);
      await reloadLinks();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function revoke(install_id: string) {
    if (!confirm(`Revoke this install link?`)) return;
    try { await api.spokeInstallLinkRevoke(install_id); await reloadLinks(); }
    catch (e: any) { setErr(e.message); }
  }
  async function copy(s: string) {
    try { await navigator.clipboard.writeText(s); }
    catch { setErr('Clipboard unavailable in this browser context — select the text and copy manually.'); }
  }

  return (
    <div className="space-y-4 pt-1">
      <form onSubmit={create} className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tenant ID</label>
          <input value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={me.role !== 'super_admin'}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent font-mono text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Spoke slot</label>
          <select value={slot} onChange={(e) => setSlot(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="single">Single node (one machine, 99.0% SLA)</option>
            <option value="master">Spoke 1 — HA master (99.9% SLA)</option>
            <option value="slave">Spoke 2 — HA slave (99.9% SLA)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Profile</label>
          <select value={profile} onChange={(e) => setProfile(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="single_node">single_node</option>
            <option value="compact_ha">compact_ha</option>
            <option value="production_ha">production_ha</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Allowed email (OTP recipient)</label>
          <input type="email" value={allowedEmail} onChange={(e) => setAllowedEmail(e.target.value)}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent font-mono text-sm" required />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Label (optional)</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100}
                 placeholder="e.g. Long Beach pilot — main rack"
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Link expires in (hours)</label>
          <input type="number" min={1} max={168} value={ttlHours} onChange={(e) => setTtlHours(parseInt(e.target.value, 10) || 24)}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div className="md:col-span-2 flex items-center gap-3 flex-wrap pt-1">
          <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Creating…' : '🔒 Create install link'}</button>
          {err && <span className="text-xs text-tnh-err">{err}</span>}
        </div>
      </form>

      {created && (
        <div className="card p-4 border-tnh-accent bg-tnh-accent/[0.04] space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-tnh-accent font-semibold">Install link ready</div>
          <div className="text-xs text-gray-500">Send the one-liner via Slack/email. Send the OTP separately via the inbox at <span className="font-mono">{created.allowed_email}</span>.</div>
          <div className="space-y-2 mt-2">
            <div>
              <div className="text-xs font-semibold mb-1 flex items-center justify-between">One-line install command <button onClick={() => copy(created.oneliner)} className="btn-ghost text-xs">Copy</button></div>
              <pre className="p-3 bg-gray-900 text-gray-100 text-xs rounded overflow-x-auto font-mono">{created.oneliner}</pre>
            </div>
            <div>
              <div className="text-xs font-semibold mb-1 flex items-center justify-between">Just the URL <button onClick={() => copy(created.public_url)} className="btn-ghost text-xs">Copy</button></div>
              <pre className="p-2 bg-gray-100 dark:bg-gray-900 text-xs rounded overflow-x-auto font-mono break-all">{created.public_url}</pre>
            </div>
          </div>
          <div className="text-xs text-gray-500">Expires {new Date(created.expires_at).toLocaleString()}. Single-use.</div>
        </div>
      )}

      <details>
        <summary className="cursor-pointer text-xs text-gray-500">How the customer uses the link</summary>
        <ol className="mt-2 list-decimal pl-5 space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
          <li>You send them <em>the one-line install command</em> (any channel — Slack, SMS, email).</li>
          <li>The OTP code goes <strong>separately</strong> to the inbox at the email you set above. Two-channel security.</li>
          <li>The customer opens a root terminal on the Ubuntu / Ubuntu Desktop host and runs the one-liner.</li>
          <li>The script prompts: <span className="font-mono">Enter the admin email this link is bound to:</span></li>
          <li>Then: <span className="font-mono">Enter the 6-digit code:</span> (delivered to that inbox)</li>
          <li>On verify, the script downloads + executes the real installer (token included). Spoke claims identity and starts heartbeating within ~10 seconds.</li>
        </ol>
      </details>

      {links.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-4 mb-2">Active install links</div>
          <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark text-sm">
            {links.map((l) => {
              const exp = new Date(l.expires_at);
              const consumed = l.used_count >= l.max_uses || l.revoked_at;
              return (
                <div key={l.install_id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs">{l.install_id}</span>
                      <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{l.spoke_slot}</span>
                      {l.revoked_at ? <span className="pill-err">revoked</span>
                       : l.used_count >= l.max_uses ? <span className="pill-warn">used</span>
                       : l.expires_at < Date.now() ? <span className="pill-err">expired</span>
                       : <span className="pill-ok">active</span>}
                      {l.label && <span className="text-xs text-gray-500">{l.label}</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      tenant <span className="font-mono">{l.tenant_id}</span> · bound to <span className="font-mono">{l.allowed_email}</span> · {l.used_count}/{l.max_uses} used · expires {exp.toLocaleString()}
                    </div>
                  </div>
                  {!consumed && me.role === 'super_admin' && (
                    <button onClick={() => revoke(l.install_id)} className="btn-ghost text-xs text-tnh-err">Revoke</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Direct-download fallback (the original flow). Kept available for air-gapped
 * customer environments where the host can't reach api.trinethub.com
 * interactively to do the OTP exchange.
 */
function DownloadInstallerPanel({ me }: { me: Me }) {
  const [slot, setSlot] = useState<'single' | 'master' | 'slave'>('single');
  const [tenantId, setTenantId] = useState<string>(me.role === 'super_admin' ? 'longbeach' : me.tenant_id);
  const [profile, setProfile] = useState<'single_node' | 'compact_ha' | 'production_ha'>('single_node');
  const [installDir, setInstallDir] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lastFilename, setLastFilename] = useState<string | null>(null);

  // Sensible profile defaults per slot
  useEffect(() => {
    if (slot === 'single' && profile !== 'single_node') setProfile('single_node');
    if (slot !== 'single' && profile === 'single_node') setProfile('production_ha');
  }, [slot]);

  async function download() {
    setBusy(true); setErr(null); setLastFilename(null);
    try {
      const body: Record<string, unknown> = { tenant_id: tenantId, spoke_slot: slot, profile };
      if (installDir.trim()) body.install_dir = installDir.trim();
      const res = await fetch(`${(import.meta.env.VITE_API_BASE ?? 'https://api.trinethub.com')}/api/spokes/install-script`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); msg = j.error ?? msg; } catch {}
        throw new Error(msg);
      }
      // Pull filename from Content-Disposition; fall back to a sensible default.
      const disp = res.headers.get('Content-Disposition') ?? '';
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `install-trinethub-spoke-${tenantId}-${slot}.sh`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setLastFilename(filename);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const slotLabel = slot === 'master' ? 'Spoke 1 (HA master)' : slot === 'slave' ? 'Spoke 2 (HA slave)' : 'Single node';

  return (
    <div className="card p-5 bg-gradient-to-br from-tnh-accent/5 to-transparent border-tnh-accent/30 space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-tnh-accent font-semibold">Download spoke installer</div>
        <div className="font-medium mt-1">Self-service: pick the slot, click Download, copy the file to the customer's Ubuntu box, run with sudo.</div>
        <div className="text-sm text-gray-500 mt-1">
          The bootstrap token is baked in. The script is single-use and expires in 24 hours.
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tenant ID</label>
          <input value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                 disabled={me.role !== 'super_admin'}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent font-mono text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Spoke slot</label>
          <select value={slot} onChange={(e) => setSlot(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="single">Single node (one machine, 99.0% SLA)</option>
            <option value="master">Spoke 1 — HA master (paired with slave, 99.9% SLA)</option>
            <option value="slave">Spoke 2 — HA slave (paired with master, 99.9% SLA)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Profile</label>
          <select value={profile} onChange={(e) => setProfile(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="single_node">single_node</option>
            <option value="compact_ha">compact_ha</option>
            <option value="production_ha">production_ha</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Install dir on customer host (optional)</label>
          <input value={installDir} onChange={(e) => setInstallDir(e.target.value)}
                 placeholder={`/opt/trinethub/spoke-${slot}`}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent font-mono text-sm" />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap pt-1">
        <button onClick={download} disabled={busy} className="btn-primary">
          {busy ? 'Generating…' : `Download installer (${slotLabel})`}
        </button>
        {lastFilename && (
          <span className="text-xs text-tnh-ok">Downloaded <span className="font-mono">{lastFilename}</span>.</span>
        )}
        {err && <span className="text-xs text-tnh-err">{err}</span>}
      </div>

      <details className="text-xs text-gray-500 pt-1">
        <summary className="cursor-pointer">How the customer uses it</summary>
        <ol className="mt-2 list-decimal pl-5 space-y-1.5">
          <li>Save the downloaded file to the Ubuntu / Ubuntu Desktop machine that will run the spoke.</li>
          <li>Open a terminal in the folder containing the file.</li>
          <li>Run: <code className="font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">sudo bash install-trinethub-spoke-{tenantId}-{slot}.sh</code></li>
          <li>The script installs Python deps, claims identity from the Hub, persists credentials under <code className="font-mono">/opt/trinethub/spoke-{slot}/state/identity.json</code>, then starts heartbeating + uploading CDR.</li>
          <li>The spoke will appear in this list within ~60 seconds, status <span className="font-semibold">operational</span>.</li>
          <li>For production: register the agent as a systemd service — the install script prints the exact one-liner at the end.</li>
        </ol>
      </details>
    </div>
  );
}

function SpokeDetailModal({ spoke, me, onClose, onRevoke }: { spoke: SpokeRow; me: Me; onClose: () => void; onRevoke: (s: SpokeRow) => void }) {
  const [detail, setDetail] = useState<any | null>(null);
  const [commands, setCommands] = useState<SpokeCommandRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [roleChoice, setRoleChoice] = useState<'single' | 'primary' | 'secondary' | 'unassigned'>(spoke.ha_role ?? 'unassigned');
  const [preempt, setPreempt] = useState<0 | 1>((spoke.preempt ?? (spoke.ha_role === 'primary' ? 1 : 0)) as 0 | 1);

  // Tenant-admin can manage their own spokes; super-admin manages all.
  const canManage = me.role === 'super_admin' || (me.role === 'tenant_admin' && me.tenant_id === spoke.tenant_id);
  const isSuper = me.role === 'super_admin';

  async function load() {
    try {
      const [d, c] = await Promise.all([api.spokesGet(spoke.spoke_id), api.spokesListCommands(spoke.spoke_id).catch(() => ({ commands: [] }))]);
      setDetail(d); setCommands(c.commands ?? []);
    } catch {}
  }
  useEffect(() => { load(); }, [spoke.spoke_id]);

  async function assignRole() {
    setBusy('role'); setActionErr(null);
    try { await api.spokesAssignHaRole(spoke.spoke_id, roleChoice, preempt); setShowRoleForm(false); await load(); }
    catch (e: any) { setActionErr(e.message); }
    finally { setBusy(null); }
  }

  async function pushCommand(type: SpokeCommandType, payload?: any) {
    setBusy(type); setActionErr(null);
    try { await api.spokesQueueCommand(spoke.spoke_id, type, payload); await load(); }
    catch (e: any) { setActionErr(e.message); }
    finally { setBusy(null); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="font-mono text-lg">{spoke.spoke_id}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {statusPill(spoke)}
              {haBadge(spoke)}
              <span className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{spoke.profile}</span>
              <span className="text-xs text-gray-500">install slot {spoke.spoke_slot} · tenant {spoke.tenant_id}</span>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost text-sm">Close</button>
        </div>

        <div className="text-sm text-gray-500 space-y-1 mb-4">
          <div>Bootstrapped: {new Date(spoke.bootstrap_at).toLocaleString()}</div>
          <div>Last heartbeat: {spoke.last_heartbeat_at ? new Date(spoke.last_heartbeat_at).toLocaleString() : 'never'} ({timeAgo(spoke.last_heartbeat_at)})</div>
          <div>Cert expires: {new Date(spoke.cert_expires_at).toLocaleString()}</div>
          <div>Config version: v{spoke.config_version}</div>
          <div>Agent version: <span className="font-mono">{spoke.agent_version || '?'}</span></div>
          {spoke.host_info && <div>Host: <span className="font-mono text-xs">{spoke.host_info}</span></div>}
          {spoke.ha_state_changed_at && (
            <div>HA state changed: {timeAgo(spoke.ha_state_changed_at)}</div>
          )}
          {spoke.error_summary && spoke.error_summary.length > 0 && (
            <div className="text-tnh-err">Last error: {spoke.error_summary}</div>
          )}
        </div>

        {/* M9: Lifecycle actions — visible to tenant_admin (own tenant) and super_admin */}
        {canManage && (
          <div className="card p-4 mb-4 bg-tnh-accent/[0.03] border-tnh-accent/30">
            <div className="text-xs font-semibold text-tnh-accent uppercase tracking-wide mb-3">Lifecycle actions (over the tunnel — no redeploy needed)</div>
            {actionErr && <div className="text-sm text-tnh-err mb-2">{actionErr}</div>}

            {/* Assign HA role + preempt */}
            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                  <span className="text-gray-500">HA role:</span> <span className="font-medium">{spoke.ha_role ?? 'unassigned'}</span>
                  {' · '}
                  <span className="text-gray-500">state:</span> <span className="font-medium">{spoke.ha_state ?? 'none'}</span>
                  {' · '}
                  <span className="text-gray-500">preempt:</span> <span className="font-medium">{spoke.preempt ?? 0}</span>
                </div>
                <button onClick={() => setShowRoleForm((v) => !v)} className="btn-ghost text-xs">
                  {showRoleForm ? 'Cancel' : 'Assign role'}
                </button>
              </div>
              {showRoleForm && (
                <div className="flex items-center gap-2 flex-wrap p-3 rounded bg-gray-50 dark:bg-gray-900">
                  <select value={roleChoice} onChange={(e) => { const v = e.target.value as any; setRoleChoice(v); setPreempt((v === 'primary' ? 1 : 0) as 0 | 1); }}
                          className="px-2 py-1.5 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
                    <option value="single">Single node</option>
                    <option value="primary">Spoke 1 — HA Primary</option>
                    <option value="secondary">Spoke 2 — HA Secondary</option>
                    <option value="unassigned">Unassigned</option>
                  </select>
                  <label className="text-xs flex items-center gap-1.5">
                    <input type="checkbox" checked={preempt === 1} onChange={(e) => setPreempt(e.target.checked ? 1 : 0)} />
                    preempt=1 (auto-recover to primary)
                  </label>
                  <button onClick={assignRole} disabled={busy === 'role'} className="btn-primary text-xs">
                    {busy === 'role' ? 'Assigning…' : 'Save & push'}
                  </button>
                </div>
              )}
            </div>

            {/* Before troubleshooting — remind operators to check Cisco status */}
            <CiscoStatusCallout scope="both" compact />

            {/* Inline command buttons */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => pushCommand('reload_config')} disabled={!!busy} className="btn-ghost text-xs">
                {busy === 'reload_config' ? '…' : '↻ Reload config'}
              </button>
              <button onClick={() => pushCommand('run_diagnostic')} disabled={!!busy} className="btn-ghost text-xs">
                {busy === 'run_diagnostic' ? '…' : '🔍 Run diagnostic'}
              </button>
              <button onClick={() => { if (confirm('Restart the agent process on this spoke?')) pushCommand('restart_agent'); }} disabled={!!busy} className="btn-ghost text-xs">
                {busy === 'restart_agent' ? '…' : '↻ Restart agent'}
              </button>
              {isSuper && (
                <button onClick={() => alert('Use the CLI: bash infra/spoke/push-agent-upgrade.sh ' + spoke.spoke_id)}
                        className="btn-ghost text-xs text-indigo-600">
                  ⬆ Push agent upgrade (super-admin)
                </button>
              )}
            </div>
            <div className="text-[11px] text-gray-500 mt-2">
              Commands queue inside the Hub and are delivered on the next heartbeat (≤60s). The agent ACKs back via the same tunnel.
            </div>
          </div>
        )}

        {/* Recent command history */}
        {commands.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent commands</div>
            <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark text-xs">
              {commands.slice(0, 10).map((c) => (
                <div key={c.id} className="px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono">#{c.id} · {c.command_type}</span>
                    <span className={
                      c.status === 'ack_ok' ? 'text-emerald-600' :
                      c.status === 'ack_failed' ? 'text-tnh-err' :
                      c.status === 'expired' ? 'text-gray-400' :
                      'text-amber-600'
                    }>{c.status}</span>
                  </div>
                  <div className="text-[11px] text-gray-500">
                    by {c.issued_by} · {timeAgo(c.issued_at)}
                    {c.ack_at ? ` · acked ${timeAgo(c.ack_at)}` : c.delivered_at ? ` · delivered ${timeAgo(c.delivered_at)}` : ''}
                  </div>
                  {c.ack_payload && <div className="text-[11px] text-gray-500 mt-1 font-mono truncate">{c.ack_payload}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {detail?.recent_heartbeats && detail.recent_heartbeats.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent heartbeats</div>
            <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark text-xs">
              {detail.recent_heartbeats.map((h: any, i: number) => (
                <div key={i} className="px-3 py-2 flex justify-between">
                  <span className="font-mono">{new Date(h.received_at).toLocaleString()}</span>
                  <span>{h.overall} · v{h.spoke_version}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isSuper && (
          <div className="flex justify-end mt-5">
            <button onClick={() => onRevoke(spoke)} className="btn-ghost text-sm text-tnh-err">Revoke spoke</button>
          </div>
        )}
      </div>
    </div>
  );
}
